<template>
  <div
    v-if="!$store.state.isNewPresentation"
    class="vertical layout slide-type-container"
    :class="{
      'slide-presenting-mode': isPresenting || isBackstage,
      'slide-has-quiz': slideActive.type == 'pickAnswer',
      'slide-text-left': slideActive.textAlign == 'left',
      'slide-text-center': slideActive.textAlign == 'center',
      'slide-text-right': slideActive.textAlign == 'right',
      'enable-audio-control': slideActive.audioShowControl
    }"
  >
    <div
      class="exit-presenting-mode-overlay"
      id="slide-exit-overlay"
      :class="{ 'overlay-presenting': isPresenting, 'white': isTextColourLight }"
      v-if="!sharePresentation"
    />
    <BackToEditorButton v-if="isBackToEditorButtonVisible" />
    <QuizMusicToggle v-if="isQuizMusicToggleVisible" />
    <FreestyleAudioToggle />
    <PresentationMode
      :presentation="presentation"
      v-if="isPrivateModeVisible"
      isCanvasButton
      class="private-mode"
      :class="{ 'has-quiz-music-toggle': isQuizMusicToggleVisible }"
    />
    <InstructionBar />
    <transition name="fade">
      <div
        class="has-picked-submission"
        v-if="isPickedSubmissionBackgroundVisible"
        @click="_handleRemovePickOption()"
      />
    </transition>
    <canvas id="firework-canvas" v-show="showFireworkCanvas" />
    <CanvasLogo />
    <EffectWrapper
      v-if="showEffectWrapper && !isSharingView"
      :mode="showEffectMode"
      @hideEffectWrapper="hideEffectWrapper"
    />

    <!-- add animation when next slide content -->
    <SlideContent
      class="flex vertical layout slide-content--wrapper animate__animated"
      id="slide-content--wrapper"
      :class="{
        'contentFadeInLeft': $store.state.transitionContent == 'left',
        'contentFadeInRight': $store.state.transitionContent == 'right'
      }"
      :showSoloRanking="showSoloRanking"
      :showCarouselSolo="showCarouselSolo"
      :showCarouselTeam="showCarouselTeam"
      :showWinnerId="showWinnerId"
      @updateSlide="_updateSlide"
      @toggleShowCarousel="toggleShowCarousel"
      @handleShowWinner="handleShowWinner"
    />

    <FreestyleCanvasContentV2 v-if="slideActive.type === 'freestyle'" />

    <!-- End add animation when next slide content -->

    <div
      class="bottom-slide"
      :class="{
        'is-content-slide': isContentSlide,
        'is-screen-capture': isCaptureContentSlideThumbnail,
      }"
    >
      <div
        class="relative list-tooltip horizontal layout center center-justified"
        :class="{'oe-has-pagination': openEndedHasPagination}"
      >
        <CanvasUserIsTyping
          v-if="isActiveUserTypingFeature && !isScreenCapture"
          class="absolute right-0 bottom-1 z-1"
        />
        <CountDown v-if="hasTimeLimitOption && showCountDown" />
        <ShowCorrectOptionSticker
          v-if="isShowCorrectOptionStickerVisible"
          class="item-slide-tooltip"
          @updateSlide="_updateSlide"
        />
        <HideQuestionImageSticker
          v-if="_isShowQuestionImageSticker()"
          class="text-center item-slide-tooltip hide-question-image"
          @updateSlide="_updateSlide"
        />
        <ConnectionLostSticker
          class="item-slide-tooltip lost-connection"
          v-if="isConnectionLostStickerVisible"
        />
        <HideResultsSticker
          v-if="isHideResultStickerVisible"
          class="hide-result item-slide-tooltip"
          @updateSlide="_updateSlide"
        />
        <StopSubmissionSticker
          class="stop-submission item-slide-tooltip"
          v-if="isStopSubmissionStickerVisible"
          @updateSlide="_updateSlide"
        />
        <BrainstormVotingSticker v-if="isBrainstormVotingStickerVisible" />

        <div
          class="text-center item-slide-tooltip hide-question-image"
          v-if="slideActive.type == 'leaderboard' && showSwapRanking"
        >
          <button class="btn btn-outline-dark btn-sm" @click="swapRanking">
            <span>
              <span v-if="showSoloRanking">{{ $t('Show Team Ranking') }}</span>
              <span v-else>{{ $t('Show Individual Ranking') }}</span>
            </span>
          </button>
        </div>
        <div
          class="text-center item-slide-tooltip hide-question-image"
          v-if="isTypeAnswerButtonVisible"
        >
          <button class="btn btn-outline-dark btn-sm" @click="swapAcceptedTypeAnswer()">
            <span>
              <span v-if="!slideActive.showAcceptedAnswerView">
                {{ $t('Accept more answers') }}
              </span>
              <span v-else>
                {{ $t('See full results') }}
              </span>
            </span>
          </button>
        </div>

        <div
          class="text-center item-slide-tooltip hide-question-image show-winner"
          v-if="slideActive.type == 'leaderboard'"
        >
          <button
            class="btn btn-outline-dark btn-sm"
            @click="toggleShowCarousel(
              !presentation.teamPlay || showSoloRanking ? 'solo' : 'team')"
            v-if="showWinner"
          >
            <span>{{ $t('Show Winner') }}</span>
          </button>
          <span v-else>&nbsp;</span>
        </div>
      </div>

      <div class="pt-1 horizontal layout center">
        <div class="flex" />
        <div
          class="horizontal layout center"
          :class="{
            'white': isTextColourLight,
            'no-blur': sharePresentation, 'x': !isPresenting,
            'canvas-pill': isCanvasPillClassVisible
          }"
        >
          <ReactionCanvas v-if="showReactionCanvas" class="relative z-10" />
          <VoteCount class="relative z-10" v-if="isVoteCountVisible" />
          <OnlineAnswerCount class="relative z-10" v-if="!isScreenCapture" />
        </div>
      </div>
    </div>
    <SlideProgressBar v-if="isPresenting" />
  </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'

import _ from 'lodash'
import ReactionCanvas from '@/components/presentations/ReactionCanvas.vue'
import OnlineAnswerCount from '@/components/presentations/OnlineAnswerCount.vue'
import SlideProgressBar from '@/components/presentations/SlideProgressBar.vue'
import CanvasLogo from '@/components/presentations/CanvasLogo.vue'
import CountDown from '@/components/presentations/CountDown.vue'
import VoteCount from '@/components/presentations/VoteCount.vue'
import BackToEditorButton from '@/components-v2/presentation-editor/middle/BackToEditorButton.vue'
import QuizMusicToggle from '@/components-v2/presentation-editor/middle/QuizMusicToggle.vue'
import FreestyleAudioToggle from '@/components-v2/presentation-editor/middle/FreestyleAudioToggle.vue'
import PresentationMode from '@/components-v2/my-presentations/main-table/PresentationMode.vue'
import InstructionBar from '@/components-v2/presentation-editor/middle/InstructionBar.vue'
import SlideContent from '@/components-v2/presentation-editor/middle/SlideContent.vue'
import CanvasUserIsTyping from '@/components-v2/presentation-editor/middle/CanvasUserIsTyping.vue'
import FreestyleCanvasContentV2 from '@/components-v2/presentation-editor/freestyle/FreestyleCanvasContent.v2.vue'

// stickers
import ShowCorrectOptionSticker from '@/components-v2/presentation-editor/middle/stickers/ShowCorrectOptionSticker.vue'
import HideQuestionImageSticker from '@/components-v2/presentation-editor/middle/stickers/HideQuestionImageSticker.vue'
import ConnectionLostSticker from '@/components-v2/presentation-editor/middle/stickers/ConnectionLostSticker.vue'
import HideResultsSticker from '@/components-v2/presentation-editor/middle/stickers/HideResultsSticker.vue'
import StopSubmissionSticker from '@/components-v2/presentation-editor/middle/stickers/StopSubmissionSticker.vue'
import BrainstormVotingSticker from '@/components-v2/presentation-editor/middle/stickers/BrainstormVotingSticker.vue'

import EffectWrapper from '@/components-v2/presentation-editor/middle/effects/EffectWrapper.vue'

import Config from '@/config'
import Util from '@/util'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import QuizStatuses from '@/constant/QuizStatuses'

export default {
  components: {
    ReactionCanvas,
    OnlineAnswerCount,
    SlideProgressBar,
    CanvasLogo,
    CountDown,
    VoteCount,
    BackToEditorButton,
    QuizMusicToggle,
    FreestyleAudioToggle,
    PresentationMode,
    InstructionBar,
    SlideContent,
    CanvasUserIsTyping,

    // stickers
    ShowCorrectOptionSticker,
    HideQuestionImageSticker,
    ConnectionLostSticker,
    HideResultsSticker,
    StopSubmissionSticker,
    BrainstormVotingSticker,
    FreestyleCanvasContentV2,
    EffectWrapper,
  },
  data() {
    return {
      userCountAnimation: false,
      questionAnimate: false,
      showSoloRanking: false,
      showCarouselTeam: true,
      showCarouselSolo: true,
      showWinnerId: null,
      QuizStatuses,
      openEndedHasPagination: false,
      showEffectWrapper: false,
      showEffectMode: {
        drumRoll: false,
        confetti: false,
        handClap: false,
      },
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      currentUser: (state) => state.currentUser,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
      isPlayDemoMode: (state) => state.isPlayDemoMode,
      isScreenCapture: (state) => state.isScreenCapture,
      isCaptureContentSlideThumbnail: (state) => state.isCaptureContentSlideThumbnail,
    }),

    isPresentingOrBackstage() {
      return this.isPresenting || this.isBackstage
    },

    isActiveUserTypingFeature() {
      const isActiveSlideType = [
        'ideas',
        'wordCloud',
        'openEnded',
        'pickAnswer',
        'question',
      ].includes(this.slideActive.type)

      return isActiveSlideType &&
        this.isPresentingOrBackstage &&
        !this.sharePresentation
    },

    showCountDown() {
      return (this.isPresenting || this.isBackstage) &&
        this.slideActive.id &&
        !this.sharePresentation
    },

    showFireworkCanvas() {
      return this.slideActive.type === 'leaderboard'
        || this.slideActive.type === 'spinnerWheel'
        || this.slideActive.type === 'ideas'
    },
    hasTimeLimitOption() {
      return this.slideActive.type === 'imageChoice' ||
        this.slideActive.type === 'multipleChoice' ||
        this.slideActive.type === 'openEnded' ||
        this.slideActive.type === 'wordCloud' ||
        this.slideActive.type === 'scale'
    },
    isContentSlide() {
      return this.slideActive && this.slideActive.type === 'freestyle'
    },

    showSwapRanking() {
      return this.presentation.teamPlay
        && this.slideActive.id
        && this.slideActive.type === 'leaderboard'
        && !this.isBackstage
    },
    showWinner() {
      const showSoloWinner = (!this.presentation.teamPlay || this.showSoloRanking)
        && !this.showCarouselSolo
      const showTeamWinner = !this.showSoloRanking && !this.showCarouselTeam

      return this.showWinnerId
        && this.slideActive.type === 'leaderboard'
        && (this.showWinnerId === this.slideActive.id)
        && (showSoloWinner || showTeamWinner)
    },
    isBrainstormVotingStickerVisible() {
      return this.slideActive.type === 'ideas' && !this.sharePresentation
    },
    isTextColourLight() {
      return this.slideActive.textColour === '#ffffff'
    },
    isHideResultStickerVisible() {
      const slideTypes = ['multipleChoice', 'imageChoice', 'openEnded', 'wordCloud', 'scale']
      return slideTypes.includes(this.slideActive.type) &&
        this.slideActive.hideResult && !this.sharingPresentation
    },
    isPickedSubmissionBackgroundVisible() {
      return this.slideActive.hasPickedSubmission &&
        (this.slideActive.type === 'ideas' ||
          (this.slideActive.type === 'openEnded' && this.slideActive.layout === 'grid'))
    },
    showReactionCanvas() {
      const {
        numberOfLikes, numberOfHearts, numberOfLaughs, numberOfWows, numberOfSads,
      } = this.presentation
      const hasReactions = numberOfLikes > 0 || numberOfHearts > 0 ||
        numberOfLaughs > 0 || numberOfWows > 0 || numberOfSads > 0
      return this.presentation.id && hasReactions
    },
    isBackToEditorButtonVisible() {
      return !this.sharePresentation && this.isPresenting && !this.isPlayDemoMode
    },
    isQuizLobbyScreen() {
      const { type, quizStatus } = this.slideActive
      return type === 'pickAnswer' && quizStatus === QuizStatuses.LOBBY_SCREEN
    },
    isQuizMusicToggleVisible() {
      return this.isPresenting &&
        !this.sharePresentation &&
        ((this.isQuizLobbyScreen) ||
          (this.slideActive.type === 'leaderboard' &&
            !this.slideActive.audioLink))
    },

    isPrivateModeVisible() {
      return this.presentation.privateMode && this.isBackToEditorButtonVisible
    },
    isQuizSlideType() {
      const { id, type } = this.slideActive
      return id && type == 'pickAnswer'
    },
    isQuizShowResultStickerVisible() {
      return this.isQuizSlideType && (this.isPresenting || this.isBackstage)
        && this.slideActive.quizStatus === QuizStatuses.QUESTION_SCREEN
        && !this.presentation.audiencePacing
        && !this.sharePresentation
    },
    isRevealCorrectAnswerVisible() {
      return this.isQuizSlideType && (this.isPresenting || this.isBackstage) &&
        this.presentation.manualRevealCorrectAnswers &&
        this.slideActive.quizStatus >= QuizStatuses.QUESTION_SCREEN &&
        !this.sharePresentation &&
        !this.slideActive.showCorrectOption &&
        this.slideActive.isShowQuizResultButton
    },
    isTypeAnswerButtonVisible() {
      const {
        type, slideType, quizStatus, showCorrectOption,
      } = this.slideActive
      const { manualRevealCorrectAnswers } = this.presentation
      return type === 'pickAnswer'
        && slideType === 'typeAnswer' &&
        quizStatus == QuizStatuses.RESULT_SCREEN &&
        ((this.isPresentingOrBackstage && !manualRevealCorrectAnswers)
          || (manualRevealCorrectAnswers && showCorrectOption))
    },
    isShowCorrectOptionStickerVisible() {
      const { addCorrectOption, type } = this.slideActive
      return addCorrectOption && !this.sharePresentation
        && (type == 'multipleChoice' || type == 'imageChoice')
    },
    isStopSubmissionStickerVisible() {
      const slideTypes = ['imageChoice', 'multipleChoice', 'openEnded', 'wordCloud', 'scale']
      const { type, stopSubmission } = this.slideActive
      return slideTypes.includes(type) && !this.sharePresentation && stopSubmission
    },
    isConnectionLostStickerVisible() {
      return this.isPresenting &&
        (this.$store.state.isSyncing == 3 || this.$store.state.socketDisconnect)
    },
    isVoteCountVisible() {
      const slideTypes = ['scale', 'multipleChoice', 'imageChoice', 'pickAnswer', 'wordCloud', 'openEnded', 'ideas', 'question']
      const qnaAllSlide = this.presentation.qnaAllSlide == 'true' ||
            this.presentation.qnaAllSlide === true
      const noQnASlideType = ['pickAnswer', 'leaderboard']
      return slideTypes.includes(this.slideActive.type) ||
        (qnaAllSlide && !noQnASlideType.includes(this.slideActive.type))
    },
    isCanvasPillClassVisible() {
      return this.showReactionCanvas || this.isVoteCountVisible || !this.isScreenCapture
    },
    isSharingView() {
      return this.$store.state.sharePresentation
    },
  },

  watch: {
    slideActive() {
      this.setDefaultTextAlignment()
    },
  },
  mounted() {
    this.setDefaultTextAlignment()
    EventBus.$on('open-ended: has-pagination', (hasPagination) => {
      this.openEndedHasPagination = hasPagination
    })
    EventBus.$on('onKeyboard', (e) => {
      this.keyEffectListener(e)
    })
    EventBus.$on('effect-menu-clicked', (key) => {
      this.menuEffectItemListener(key)
    })
  },
  destroyed() {
    EventBus.$off('open-ended: has-pagination')
    EventBus.$off('effect-menu-clicked')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    setDefaultTextAlignment() {
      const { textAlign, type } = this.slideActive
      if (textAlign) return
      const slideTypes = ['bulletPoints', 'textSlide', 'youtube', 'leaderboard']
      if (slideTypes.includes(type)) {
        this.updateSlideActiveAction({
          textAlign: 'center',
        })
      } else {
        this.updateSlideActiveAction({
          textAlign: 'left',
        })
      }
    },
    swapRanking() {
      this.showSoloRanking = !this.showSoloRanking
      Util.gaTrackEvent(
        'Presentation Editor',
        `Leaderboard > Toggle Ranking > ${this.showSoloRanking ? 'Solo' : 'Team'} mode`,
        `Presentation: ${this.presentation.id}`,
      )
      this.$store.state.swapLeaderboard = true
    },

    _updateSlide(params, type) {
      this.$store.dispatch('updateSlideActive', this.slideActive)
      params.socketId = Socket.getSocketId()
      this.$store.dispatch('setSyncPresentation', 1)
      params.presenting = this.isPresenting
      this.$http
        .put(Config.api.updateSlide + this.slideActive.id, params)
        .then(
          () => {
            if (type == 1) {
              Util.gaTrackEvent(
                'Slide',
                'Edit setting',
                'Presenter edit setting hide result',
                this.slideActive.hideResult,
              )
            } else if (type == 2) {
              Util.gaTrackEvent(
                'Slide',
                'Edit setting',
                'Presenter edit setting show correct option',
                this.slideActive.showCorrectOption,
              )
            } else if (type == 3) {
              Util.gaTrackEvent(
                'Slide',
                'Edit setting',
                'Presenter edit setting stop submission',
                this.slideActive.stopSubmission,
              )
            } else if (type == 4) {
              Util.gaTrackEvent(
                'Slide',
                'Edit setting',
                'Presenter edit setting show question image',
                this.slideActive.showQuestionImage,
              )
            }
            this.$store.dispatch('setSyncPresentation', 2)
          },
          () => {
            this.$store.dispatch('setSyncPresentation', 3)
          },
        )
    },

    _setDescColor() {
      if (!this.slideActive.titleDesc) return false

      // Change base color form HEX to RGBA
      // Background's alpha = 0.2
      const colorToRgba = Util.hexToRgba(this.slideActive.baseColour)

      if (!colorToRgba) return false

      return Util.changeRgbaColor(colorToRgba, '0.85')
    },

    _isShowQuestionImageSticker() {
      if (this.$store.state.isScreenCapture) {
        return false
      }
      const slideTypes = ['imageChoice', 'multipleChoice', 'openEnded', 'wordCloud', 'question', 'scale']
      const { slideActive } = this.$store.state
      const presenting = this.isPresenting || this.isBackstage
      let show = false

      if (slideActive.image) {
        slideTypes.forEach((type) => {
          if (slideActive.type === type) {
            show = true
          }
        })
        if ((slideActive.type === 'pickAnswer' && !presenting) || (slideActive.type === 'pickAnswer' && presenting && this.sharePresentation)) {
          show = true
        }
      }
      return show
    },

    toggleShowCarousel(type, id) {
      let show
      if (type === 'solo') {
        this.showCarouselSolo = !this.showCarouselSolo
        show = this.showCarouselSolo
      } else {
        this.showCarouselTeam = !this.showCarouselTeam
        show = this.showCarouselTeam
      }
      this.showWinnerId = id
      Util.gaTrackEvent(
        'Presentation Editor',
        `Leaderboard > ${show ? 'Show' : 'Hide'} Winner`,
        `Presentation: ${this.presentation.id}, mode: ${type}`,
      )
    },

    handleShowWinner(id) {
      this.showWinnerId = id
    },

    swapAcceptedTypeAnswer() {
      this.updateSlideActiveAction({
        showAcceptedAnswerView: !this.slideActive.showAcceptedAnswerView,
      })
      Util.gaTrackEvent(
        'Presentation Editor',
        `Type Answer > toggle show accepted answer ${this.slideActive.showAcceptedAnswerView}`,
        'Toggle show accepted answer',
      )
    },

    _handleRemovePickOption() {
      EventBus.$emit('handleRemovePickOption')
    },
    // eslint-disable-next-line func-names
    keyEffectListener: _.throttle(function (evt) {
      if (evt.target.value) return
      if (evt.target.tagName !== 'BODY') return
      const { isPresenting } = this.$store.getters
      const mechamism = 'Shortkey'
      if (isPresenting) {
        if (evt.keyCode === 67 && !this.showEffectMode.drum && !this.showEffectMode.hand) {
          this.showEffectMode.confetti = !this.showEffectMode.confetti
          this.showEffectWrapper = (this.showEffectMode.confetti === true)
          if (this.showEffectWrapper) {
            this.effectTriggedTracker('Confetti', mechamism)
            // eslint-disable-next-line sonarjs/no-duplicate-string
            EventBus.$emit('effect-wrapper-showed', 'c')
          // eslint-disable-next-line sonarjs/no-duplicate-string
          } else EventBus.$emit('effect-wrapper-hided')
        } else if ((evt.keyCode === 68 || evt.key === 'đ' || evt.key === 'd') &&
          !this.showEffectMode.confetti && !this.showEffectMode.hand) {
          this.showEffectMode.drum = !this.showEffectMode.drum
          this.showEffectWrapper = (this.showEffectMode.drum === true)
          if (this.showEffectWrapper) {
            this.effectTriggedTracker('Drumroll', mechamism)
            EventBus.$emit('effect-wrapper-showed', 'd')
          } else EventBus.$emit('effect-wrapper-hided')
        } else if (evt.keyCode === 72 &&
          !this.showEffectMode.confetti && !this.showEffectMode.drum) {
          this.showEffectMode.hand = !this.showEffectMode.hand
          this.showEffectWrapper = (this.showEffectMode.hand === true)
          if (this.showEffectWrapper) {
            this.effectTriggedTracker('Hand Clap', mechamism)
            EventBus.$emit('effect-wrapper-showed', 'h')
          } else EventBus.$emit('effect-wrapper-hided')
        }
      }
    }, 500),
    // eslint-disable-next-line func-names, prefer-arrow-callback
    menuEffectItemListener: _.throttle(function (key) {
      const { isPresenting } = this.$store.getters
      const mechamism = 'Effect legend'
      if (isPresenting) {
        if (key === 'c' && !this.showEffectMode.drum && !this.showEffectMode.hand) {
          this.showEffectMode.confetti = !this.showEffectMode.confetti
          this.showEffectWrapper = (this.showEffectMode.confetti === true)
          if (this.showEffectWrapper) this.effectTriggedTracker('Confetti', mechamism)
          else EventBus.$emit('effect-wrapper-hided')
        } else if (key === 'd' && !this.showEffectMode.confetti && !this.showEffectMode.hand) {
          this.showEffectMode.drum = !this.showEffectMode.drum
          this.showEffectWrapper = (this.showEffectMode.drum === true)
          if (this.showEffectWrapper) this.effectTriggedTracker('Drumroll', mechamism)
          else EventBus.$emit('effect-wrapper-hided')
        } else if (key === 'h' && !this.showEffectMode.confetti && !this.showEffectMode.drum) {
          this.showEffectMode.hand = !this.showEffectMode.hand
          this.showEffectWrapper = (this.showEffectMode.hand === true)
          if (this.showEffectWrapper) this.effectTriggedTracker('Hand Clap', mechamism)
          else EventBus.$emit('effect-wrapper-hided')
        }
      }
    }, 500),
    hideEffectWrapper() {
      this.showEffectMode = {
        drum: false,
        confetti: false,
        hand: false,
      }
      this.showEffectWrapper = false
      EventBus.$emit('effect-wrapper-hided')
    },
    effectTriggedTracker(label, mechanism) {
      const { type, slideType } = this.slideActive
      const slideName = Util.getCorrectSlideTypeName(type, slideType)
      Util.gaTrackEvent('Presenting Mode', 'Effects triggered', label, '', {
        trigger_mechanism: mechanism,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        slideType: slideName,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.audio-control {
  padding: 0 16px;
}

.slide-content--wrapper {
  ::v-deep .text-hyper-link {
    color: inherit;
    text-decoration: underline;
    opacity: 0.8;

    &:hover {
      opacity: 1;
      text-decoration: underline !important;
      color: inherit !important;
    }
  }

  &.animate__animated {
    animation-duration: 0.3s;

    &.contentFadeInLeft {
      animation: contentFadeInLeft cubic-bezier(0.25, 1, 0.5, 1) .3s;
    }

    &.contentFadeInRight {
      animation: contentFadeInRight cubic-bezier(0.25, 1, 0.5, 1) .3s;
    }
  }

  &::v-deep.audio-title-slide ::v-deep {
    audio {
      position: relative;
      z-index: 1;
    }
  }
}

.title-question-slide {
  min-height: 56px
}

.slide-content {
  width: 98%;
  margin: 0 auto;
  position: relative;

  // backface-visibility: hidden;
  &:hover {
    .title-desc-slide {
      opacity: 1;
      visibility: visible;
      z-index: 200;
    }
  }

  &.no-height {
    height: 0;
    padding: 0 !important;
  }

  .title-desc-slide {
    opacity: 0;
    visibility: hidden;
    padding: 10px;
    transition: opacity 0.2s ease;
    -webkit-transition: opacity 0.2s ease;
    position: absolute;
    top: 100%;
    z-index: 0;
    border-radius: 8px;
    font-size: 105%;
    cursor: auto;
    left: 0;
    width: 60%;

    &:hover {
      opacity: 0;
      z-index: 0;
    }
  }

  .title-slide {
    font-weight: bold;

    .title-md-fontsize {
      font-size: 47px;
    }

    .title-sm-fontsize {
      font-size: 38px;
    }

    .title-xs-fontsize {
      font-size: 34px;
    }

    &.is-exporting-pdf .title-xs-fontsize {
      font-size: 28px;
    }

    .no-show {
      opacity: 0;
    }
  }
}

.img-logo-white {
  display: none;
}

.list-tooltip {
  width: 100%;
  flex-wrap: wrap;

  .item-slide-tooltip {
    padding: 0 8px;
    border-radius: 50px;
    font-weight: bold;
    margin-right: 5px;
    font-size: 1rem;
    white-space: nowrap;
    margin-bottom: 3px;

    &:last-child {
      margin-right: 0;
    }

    .btn,
    &::v-deep .btn {
      font-weight: bold;
      font-size: 16px;
      padding: 2px 15px;
      margin: -3px 0 0 0;
      border: 1px solid rgba(0, 0, 0, 0.3);
    }

    &::v-deep.hide-result,
    &::v-deep.stop-submission,
    &.stop-submission,
    &::v-deep.lost-connection,
    &::v-deep.hide-question-image,
    .hide-question-image {
      .btn {
        background-color: #fff;

        &:hover {
          background-color: $primary-color;
          color: #ffffff;
        }

        &:focus {
          box-shadow: none;
        }
      }
    }

    &.voting-sticker {
      .btn {
        background-color: $primary-color;

        &:hover {
          background-color: $primary-hover-color;
        }
      }

      .btn:disabled {
        background-color: $primary-color;
        cursor: not-allowed;
      }
    }
  }
   &.oe-has-pagination {
    min-height: 30px;
   }
}

.bottom-slide {
  z-index: 119;
  padding: 16px;
  position: relative;
  padding-top: 4px;
  &.is-content-slide {
    padding: 4px 16px 16px;
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    &.is-screen-capture {
      display: none;
    }
  }
}

b,
strong {
  font-weight: bold;
}

.slide-hover {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;

  .main-hover {
    background-color: none;

    img {
      width: 100%;
      position: absolute;
      top: 20%;
      left: 0;
      border-radius: 5px;
    }
  }
}

.slide-type-container {
  height: 100%;
  position: relative;
  z-index: 20;
  overflow: hidden;
}

.new-slide {
  text-align: center;
  font-size: 22px;
  font-weight: bold;
  padding-top: 57px;
}

.top-sticker {
  position: absolute;
  left: 0;
  right: 0;
  width: 100%;
  padding: 10px;
  z-index: 999;

  .list-tooltip {
    width: auto;

    .item-slide-tooltip,
    &::v-deep.item-slide-tooltip {
      background-color: #fff;
      border: 1px solid #ddd;
      border-radius: 5px;
    }
  }
}

.slide-presenting-mode {

  .item-slide-tooltip,
  &::v-deep.item-slide-tooltip {
    .btn {
      border-radius: 25px;
    }
  }

  .slide-content .title-slide .title-slide-text {
    cursor: default;
  }

  &::v-deep .top-header-acode {
    &:hover {
      color: unset;
    }

    .fas.fa-pencil-alt {
      display: none;
    }
  }

  &::v-deep {
    .mce-editor {
      cursor: default;
      border: none;
      pointer-events: none;
    }
  }
}

.slide-down-enter-active {
  transition: all 0.2s ease;
}

.slide-down-leave-active {
  transform: translateY(20px);
  opacity: 0;
}

.slide-down-enter {
  opacity: 0;
  transform: translateY(-20px);
}

canvas#firework-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: $highest-z-index - 1;
  pointer-events: none;
}

.arrow-right-triangle {
  width: 0;
  height: 0;
  border-top: 11px solid transparent;
  border-bottom: 11px solid transparent;
  border-left: 11px solid;
  position: absolute;
  top: 3.25px;
  left: 20px;
  z-index: 9;
}

.slide-type-container {
  & ::v-deep .canvas-bullet-points .audio-control-slide {
    text-align: center;
  }

  & ::v-deep .textSlide-title .audio-control-slide {
    text-align: center;
  }
}

.slide-text-right {
  & ::v-deep {
    .bullet-subheading {
      width: 100%;
      text-align: right;
    }
    .title-slide-text, .bullet-heading, .count-player, .youtube-caption-text, .ideas-show-image {
      text-align: right;
    }

    .bullet-heading {
      margin-right: -5px;
    }

    .textSlide-title {
      .textSlide-heading {
        width: 100%;
        text-align: right;
        margin-right: -24px;
      }

      .textSlide-subheading {
        width: 100%;
        text-align: right;
      }
    }

    .title-desc-slide {
      left: unset;
      right: 0;
      text-align: right;
    }

    .audio-control-slide {
      text-align: right !important;

      .audio-control {
        -moz-justify-content: flex-end;
        justify-content: flex-end;
      }
    }
  }
}

.slide-text-left {
  & ::v-deep {
    .bullet-subheading {
      width: 100%;
      text-align: left;
    }
    .title-slide-text, .bullet-heading, .count-player, .youtube-caption-text, .ideas-show-image {
      text-align: left;
    }

    .title-desc-slide {
      left: 0;
      right: unset;
      text-align: left;
    }

    .bullet-heading {
      margin-left: -5px;
    }

    .textSlide-title {
      .textSlide-heading {
        width: 100%;
        text-align: left;
        margin-left: -24px;
      }

      .textSlide-subheading {
        width: 100%;
        text-align: left;
      }
    }

    .audio-control-slide {
      text-align: left !important;

      .audio-control {
        justify-content: flex-start;
      }
    }
  }

  & ::v-deep .canvas-bullet-points .audio-control-slide {
    text-align: left !important;
  }
}

.slide-text-center {
  & ::v-deep {
    .title-slide-text, .bullet-heading, .count-player, .youtube-caption-text, .ideas-show-image {
      text-align: center;
    }

    .title-desc-slide {
      text-align: center;
      left: 20%;
      right: unset;
    }

    .audio-control-slide {
      text-align: center;

      .audio-control {
        -moz-justify-content: center;
        justify-content: center;
      }
    }
  }
}

.show-winner {
  line-height: 1.5;
}

.spinner-wheel-title {
  min-height: 56px;
}

.canvas-pill {
  padding: 8px 10px;
  position: relative;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 27px;

  &.white {
    background: rgba(0, 0, 0, 0.36);
  }
}

.has-picked-submission {
  background-color: rgba(0, 0, 0, 0.6);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 128;
}

.private-mode {
  position: absolute;
  cursor: pointer;
  top: 0.5rem;
  left: 4rem;
  font-size: 1.4rem;
  z-index: 135;
  opacity: 0;
  transition: all 0.3s ease;

  &::v-deep {
    .private-mode__wrapper {
      border-radius: 9999px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      background-color: #EBEBEB;
      backdrop-filter: blur(44px);

      &:hover {
        background-color: #CECECE;
      }

      &.light-text {
        background-color: rgba(0, 0, 0, 0.36);
      }

      &:hover.light-text {
        background-color: rgba(0, 0, 0, 0.5);
      }
    }
  }

  &.has-quiz-music-toggle {
    left: 8rem;
  }
}
</style>
